/**
 * Hook for loading and managing client data
 */

import { useState, useEffect, useMemo } from 'react';
import { 
  loadClientData, 
  processCountryData, 
  filterCountryData, 
  filterClientData 
} from '../services/dataService';
import { getPortfolioMetadata } from '../services/api/fileMetadataApi';

/**
 * Custom hook for loading and managing client data
 * @returns {Object} Client data state and utility functions
 */
const useClientData = () => {
  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientData, setClientData] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [totals, setTotals] = useState({ entries: 0, countries: 0, managers: 0 });
  const [managerList, setManagerList] = useState([]);
  const [fileMetadata, setFileMetadata] = useState(null);
  const [conferenceSalesData, setConferenceSalesData] = useState({});
  
  // Zoom state
  const [zoom, setZoom] = useState(3); 

  // Filters state
  const [selectedManager, setSelectedManager] = useState('');
  const [minTotal, setMinTotal] = useState(0);
  const [minLargest, setMinLargest] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('All');

  // Load data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // --- FIX: `loadClientData` now returns the array directly ---
        // We no longer need to destructure `data` from a result object.
        const clients = await loadClientData();
        
        // Ensure we received a valid array before proceeding
        if (Array.isArray(clients)) {
          setClientData(clients);
        
          // Extract conference sales data if available
          if (clients.conferenceSalesData) {
            setConferenceSalesData(clients.conferenceSalesData);
            console.log('Conference sales data loaded in useClientData:', {
              clientCount: Object.keys(clients.conferenceSalesData).length
            });
          }
        
          // Process country data with the valid client list
          const { countryData, totals, allManagers } = processCountryData(clients);
          setCountryData(countryData);
          setTotals(totals);
          setManagerList(allManagers);
        } else {
            // If we don't get an array, something went wrong during loading.
            throw new Error("Client data could not be loaded correctly.");
        }
        
        // Also fetch file metadata
        try {
          const metadata = await getPortfolioMetadata();
          setFileMetadata(metadata);
        } catch (metadataError) {
          console.warn('Could not fetch file metadata:', metadataError);
          // Don't fail the entire load if metadata fails
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Detect active tier filtering
  const activeTierFilter = useMemo(() => {
    if (!searchQuery?.trim()) return null;
    
    const query = searchQuery.toLowerCase().trim();
    const tierPatterns = [
      { pattern: /^tier\s*1$/i, value: '1' },
      { pattern: /^tier\s*2$/i, value: '2' },
      { pattern: /^tier\s*3$/i, value: '3' },
      { pattern: /^tier\s*vip$/i, value: 'VIP' }
    ];
    
    for (const tierPattern of tierPatterns) {
      if (tierPattern.pattern.test(query)) {
        return tierPattern.value;
      }
    }
    return null;
  }, [searchQuery]);

  // Filter client data based on search query and year
  const filteredClientData = useMemo(() => {
    return filterClientData(clientData, searchQuery, selectedYear);
  }, [clientData, searchQuery, selectedYear]);
  
  // Get countries that have clients matching the search query
  const searchMatchingCountries = useMemo(() => {
    if (!searchQuery.trim()) return null;
    if (filteredClientData.length === 0) {
      return new Set();
    }
    return new Set(filteredClientData.map(client => client.country));
  }, [filteredClientData, searchQuery]);
  
  // Filtered country data - recalculate from filtered clients when year is selected
  const filteredCountryData = useMemo(() => {
    let baseCountryData = countryData;
    
    // If a specific year is selected, recalculate country data from year-filtered clients
    if (selectedYear !== 'All' && filteredClientData.length > 0) {
      const { countryData: yearCountryData } = processCountryData(filteredClientData);
      baseCountryData = yearCountryData;
    }
    
    return filterCountryData(baseCountryData, {
      selectedManager,
      minTotal,
      minLargest,
      searchQuery,
      searchMatchingCountries,
      selectedYear
    });
  }, [countryData, filteredClientData, selectedManager, minTotal, minLargest, searchQuery, searchMatchingCountries, selectedYear]);

  // Calculate dynamic max values for sliders based on selected manager
  const sliderMaxValues = useMemo(() => {
    // Determine which dataset to use based on year selection
    const dataToAnalyze = selectedYear !== 'All' ? filteredClientData : clientData;
    
    // Filter data by selected manager if one is chosen
    let managerFilteredData = dataToAnalyze;
    if (selectedManager && selectedManager !== 'All' && selectedManager !== '') {
      managerFilteredData = dataToAnalyze.filter(client => 
        client.conference_manager === selectedManager
      );
    }

    if (managerFilteredData.length === 0) {
      return { maxGroups: 20, maxLargest: 100 }; // fallback to current hardcoded values
    }

    // Calculate max groups per country for this manager
    const countryGroups = {};
    managerFilteredData.forEach(client => {
      const country = client.country;
      if (country) {
        countryGroups[country] = (countryGroups[country] || 0) + 1;
      }
    });
    
    const maxGroups = Object.keys(countryGroups).length > 0 
      ? Math.max(...Object.values(countryGroups))
      : 5;

    // Calculate max group size for this manager
    const groupSizes = managerFilteredData
      .map(client => parseInt(client.group_size) || 0)
      .filter(size => size > 0);
    
    const maxLargest = groupSizes.length > 0 
      ? Math.max(...groupSizes)
      : 10;

    return {
      maxGroups: Math.max(maxGroups, 5), // minimum of 5 for usability
      maxLargest: Math.max(maxLargest, 10) // minimum of 10 for usability
    };
  }, [clientData, filteredClientData, selectedManager, selectedYear]);

  // Reset filters
  const resetFilters = () => {
    setSelectedManager('');
    setMinTotal(0);
    setMinLargest(0);
    setSearchQuery('');
  };

  return {
    // Data
    loading,
    error,
    clientData,
    filteredClientData,
    countryData,
    filteredCountryData,
    totals,
    managerList,
    fileMetadata,
    conferenceSalesData,
    
    // Zoom state
    zoom,
    setZoom,

    // Filters
    filters: {
      selectedManager,
      minTotal,
      minLargest,
      searchQuery,
      selectedYear
    },
    activeTierFilter,
    
    // Dynamic slider max values
    sliderMaxValues,
    
    // Setter functions
    setSelectedManager,
    setMinTotal,
    setMinLargest,
    setSearchQuery,
    setSelectedYear,
    resetFilters,
    
    // Helper function to filter pinned clients by year
    filterPinnedClientsByYear: (pinnedClients) => {
      if (selectedYear === 'All' || !pinnedClients || pinnedClients.length === 0) {
        return pinnedClients;
      }
      
      // Filter pinned clients to only include those that match the selected year
      return pinnedClients.filter(client => {
        // Use the same filtering logic as filterClientData
        const roomsColumn = `${selectedYear} Rooms`;
        const rnColumn = `${selectedYear} RN`;
        const revenueColumn = `${selectedYear} Revenue`;
        
        const roomsRaw = client[roomsColumn];
        const rnRaw = client[rnColumn];
        const revenueRaw = client[revenueColumn];
        
        // Convert to numbers, handling string values
        const rooms = typeof roomsRaw === 'string' ? parseFloat(roomsRaw) || 0 : roomsRaw;
        const rn = typeof rnRaw === 'string' ? parseFloat(rnRaw) || 0 : rnRaw;
        const revenue = typeof revenueRaw === 'string' ? parseFloat(revenueRaw.replace(/[$,]/g, '')) || 0 : revenueRaw;
        
        // Client matches the year if any of the three values are non-zero
        return rooms > 0 || rn > 0 || revenue > 0;
      });
    }
  };
};

export default useClientData;
